import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { CreateTube } from '@babylonjs/core/Meshes/Builders/tubeBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import { computeRoomFit, IDENTITY_ROOM_FIT, type FloorExtent, type Point3, type RoomFit } from '../core/RoomFit';
import type { ConnectionDto, NodeDto, NodeTypeDto, StrokeDto } from '../generated/contracts';
import { quatFromArray, vec3FromArray } from '../net/packing';
import type { NodeTransformUpdate, StrokePointUpdate, StrokeWidth } from '../net/wire';
import { StrokeWidths, toStrokeWidth } from '../net/wire';
import { ConnectionRenderer } from './ConnectionRenderer';
import { fadeAlphaAt, nextFadeStagger } from './FadeAnimation';
import { LabelRenderer } from './LabelRenderer';
import { ModelLibrary, type ModelLoadSummary } from './ModelLibrary';
import { NodeMeshFactory } from './NodeMeshFactory';
import type { RoomState, RoomStateListener } from './RoomState';
import { glowFadeOutAt, pulseScale } from './ScanGlowAnimation';

/** How far above a node's top its name plate hangs. */
const LABEL_CLEARANCE = 0.06;

/**
 * Stroke tube radius, in metres, for a `medium`-width marker — unchanged from before the
 * three-widths feature, deliberately: this is what every stroke rendered at when width was a
 * single constant, and it is still what `StrokeWidths.Medium` (and a `null`/unrecognised
 * stored width — `toStrokeWidth`'s own fallback) renders at, so nothing about an existing
 * stroke's on-screen size changes.
 *
 * Was 0.004, halved to 0.002 in exports v2 — the owner's own verdict at the time, drawing for
 * real: "the marker leaves lines that are much thicker than they would be in reality ... We
 * can go half width on the strokes and still be good." A later headset session with the
 * thinner strokes found the opposite problem: "they become a little bit flickery at longer
 * distances... we should bump it back to a middle ground between the old and new stroke
 * width." Now the midpoint of the two, 0.003, keeping the same proportion to the SVG constant
 * below. Read fresh every time a mesh is (re)built (`rebuildStrokeMesh`, below) from the
 * stroke's own stored `width`, not baked into the stroke's own wire data — the server-side SVG
 * export renders the same strokes at its own independent constants,
 * `StrokeWidthMetresMedium`/Fine/Fat in `Tunet.Api/Domain/WhiteboardSvgRenderer.cs`, kept at
 * the same fine/medium/fat ratio as this file's own three constants for the same reason; the
 * two are not unified into one shared source because one is a Babylon tube's world-space
 * radius and the other an SVG stroke-width in exported pixels, but a future change to either
 * is worth checking against the other.
 */
const STROKE_RADIUS_MEDIUM = 0.003;

/**
 * Baseline emissive scale applied to every committed stroke's own diffuse colour
 * (`rebuildStrokeMesh`) — pulled out to a named constant, where it used to be an inline
 * `color.scale(0.4)`, because the scan tool's glow feedback (below) now needs the exact same
 * value to know what "back to normal" means when it stops highlighting a stroke.
 */
const STROKE_EMISSIVE_SCALE = 0.4;

/**
 * The scan tool's own visual feedback (owner, from a headset session: *"we really need some
 * indicator for it actually working, maybe make all the lines that have been selected with the
 * scan tool glow a little? Subtle pulsing glow."*) — three states, all driven from `ScanTool`
 * through `setScanSelection`/`beginScanPulse`/`endScanPulse` below, all painted onto each
 * caught stroke's own already-existing per-stroke material (no material is ever created or
 * disposed for this — CLAUDE.md's own warning about `TransformNode.dispose()` not freeing
 * materials does not apply here because nothing new is ever allocated). Deliberately a
 * different channel from the assistant's own ceiling-light glow (`SceneRoot.applyAssistantGlow`)
 * — that one means "Gemma is listening or speaking," and reusing it here would make two
 * unrelated signals share one light.
 *
 * `STROKE_SCAN_GLOW_SCALE` — the steady emissive scale for a stroke currently inside the scan
 * line's live selection (state 1, "before the trigger is pressed") or freshly caught and about
 * to start pulsing (state 2's own starting point) — well above `STROKE_EMISSIVE_SCALE` so the
 * boost reads clearly against a stroke's real colour without changing what colour it is.
 */
const STROKE_SCAN_GLOW_SCALE = 1.6;

/** How far the pulse swings above and below `STROKE_SCAN_GLOW_SCALE` while a scan's
 *  transcription is in flight (state 2) — the pulse's own amplitude, so the caught strokes
 *  visibly breathe rather than sit at a fixed brightness for the multi-second network round
 *  trip. */
const STROKE_SCAN_PULSE_AMPLITUDE = 0.7;

/** One full pulse cycle, in seconds — slow enough to read as "still working," not a flicker. */
const STROKE_SCAN_PULSE_PERIOD_S = 1.6;

/** How long the glow takes to ease back to `STROKE_EMISSIVE_SCALE` when a scan finds nothing
 *  (state 3) — a snap back to normal reads as an error; a short ease reads as "done looking." */
const STROKE_SCAN_GLOW_FADE_MS = 350;

/** Half of `STROKE_RADIUS_MEDIUM` — see that constant's own doc for the ratio this mirrors,
 *  and `WhiteboardSvgRenderer.StrokeWidthMetresFine`'s identical ratio on the server side. */
const STROKE_RADIUS_FINE = STROKE_RADIUS_MEDIUM / 2;

/** Double `STROKE_RADIUS_MEDIUM` — see `STROKE_RADIUS_FINE`'s own doc. */
const STROKE_RADIUS_FAT = STROKE_RADIUS_MEDIUM * 2;

/** Babylon tube radius for a stroke of the given width — `medium`, `null`, or anything
 *  unrecognised all render at `STROKE_RADIUS_MEDIUM`, the same fallback `toStrokeWidth` itself
 *  already applies. */
function radiusForWidth(width: StrokeWidth): number {
  switch (width) {
    case StrokeWidths.Fine:
      return STROKE_RADIUS_FINE;
    case StrokeWidths.Fat:
      return STROKE_RADIUS_FAT;
    default:
      return STROKE_RADIUS_MEDIUM;
  }
}

/**
 * How long a removed node or stroke's mesh lingers, easing its material's alpha to zero,
 * before it is actually disposed. Subtle and short on purpose — this is feedback that
 * something is being removed, not a slow-motion moment; see plan/ for this milestone's own
 * framing ("an organic attempt at making the interface better, not just a set delay to hide
 * latency").
 */
const FADE_DURATION_MS = 450;

/** Stagger step between successive items fading out in the same short window — see
 *  `nextFadeStagger`'s own doc comment for why this ripples rather than moves in lockstep. */
const FADE_STAGGER_STEP_MS = 55;

/** Caps the stagger so a large bulk wipe does not push its last few items' fades absurdly
 *  late — beyond this, later items still fade, just with no further-growing head start. */
const FADE_STAGGER_MAX_MS = 440;

/** How long a gap between two removals resets the stagger ripple back to its start — see
 *  `nextFadeStagger`'s own doc comment. */
const FADE_STAGGER_RESET_GAP_MS = 250;

/** A mesh on its way out, still visible while its material eases toward invisible. */
interface FadingMesh {
  readonly mesh: AbstractMesh;
  readonly material: StandardMaterial;
  readonly startAlpha: number;
  readonly delayMs: number;
  readonly startedAtMs: number;
}

/**
 * Keeps the Babylon scene in step with RoomState.
 *
 * Only place that touches meshes. The state layer decides what is true; this decides what
 * that looks like.
 */
// spec: SPEC-110
export class RoomView implements RoomStateListener {
  private readonly meshes = new Map<string, AbstractMesh>();
  private readonly factory: NodeMeshFactory;
  private readonly connections: ConnectionRenderer;
  private readonly labels: LabelRenderer;

  /** The server's own current text for each node, tracked separately from whatever `labels` is
   *  actually painting right now — `labelStatusOverrides` below can mean the two differ for a
   *  while, and `setLabelStatus(nodeId, null)` needs to know what to restore. Updated on every
   *  `onNodeUpserted`, never read anywhere else. */
  private readonly nodeText = new Map<string, string>();

  /**
   * Nodes whose label plate is currently showing a transient status — "listening…",
   * "transcribing…", "didn't catch that" — instead of the server's own text
   * (`NodeLabelController`'s own voice-labelling feature; plan/open-items.md, "STT on node
   * renaming... without any feedback"). While a node's id is in this set, `onNodeUpserted`
   * leaves its plate alone regardless of what `NodeUpdated` arrives — only an explicit
   * `setLabelStatus(id, null)` (from `NodeLabelController.handleNodeUpdated`/`handleFailed`/its
   * own timeout backstop) is allowed to let the real text show again. Deliberately not "any
   * fresh update wins" — this connection's own successful transcription is the one thing that
   * happens to produce a `NodeUpdated` for a node mid-status today, but nothing here should
   * *depend* on that being the only thing that ever could; `nodeText` still tracks the freshest
   * text the whole time regardless, so the eventual explicit clear always restores the right
   * value with no second round trip.
   */
  private readonly labelStatusOverrides = new Set<string>();

  /** Committed strokes — RoomState-backed, rendered in their own real ink colour. */
  private readonly strokeMeshes = new Map<string, Mesh>();

  /**
   * In-progress strokes: your own as it is drawn, or a remote peer's while StrokePoint
   * updates arrive for it. Entirely separate from strokeMeshes/RoomState — a stroke does
   * not exist in state until it commits, and these points are never persisted, mirroring
   * applyLiveTransform's own rule for a held node's pose ("never written back to state —
   * these are dropped by the server, and the durable write happens once on release").
   * liveStrokePoints holds the accumulated points behind each mesh, since a remote
   * StrokePointUpdate carries only what is new since the sender's last flush.
   */
  private readonly liveStrokes = new Map<string, Mesh>();
  private readonly liveStrokePoints = new Map<string, Vector3[]>();

  /** Committed stroke ids currently in the scan tool's live selection (state 1) — glowing at a
   *  steady `STROKE_SCAN_GLOW_SCALE`, reapplied every call from `ScanTool.tick()` (see
   *  `setScanSelection`'s own doc). Disjoint from `scanPulseIds`/`scanFadingGlow` in practice —
   *  `ScanTool` clears this before starting a pulse — but each is independently correct if a
   *  stroke id somehow ended up in more than one at once, since every apply here is a plain,
   *  idempotent material write, not a toggle. */
  private scanSelectedIds: ReadonlySet<string> = new Set();

  /** Committed stroke ids currently pulsing because a scan's transcription is in flight (state
   *  2) — `null` when no scan is in flight. `scanPulseElapsedS` is simulated time since the
   *  pulse began, advanced by `tickScanGlow`. */
  private scanPulseIds: ReadonlySet<string> | null = null;
  private scanPulseElapsedS = 0;

  /** Stroke ids currently easing their glow back to baseline (state 3, "nothing found") —
   *  each entry's own start scale (the pulse's value at the moment the fade began) and elapsed
   *  time, so `tickScanGlow` can ease every one independently even though they all started at
   *  the same instant in practice. */
  private readonly scanFadingGlow = new Map<string, { readonly startScale: number; elapsedMs: number }>();

  private state: RoomState | null = null;

  /**
   * Canonical <-> this room's own floor (SPEC-131, `core/RoomFit.ts`). Identity until
   * `setRoomFit` is told the room's actual floorplan — `main.ts` constructs this class before
   * the room snapshot is fetched, so there is a real window with no floorplan known yet, and
   * every existing test in this project that never calls `setRoomFit` also depends on this
   * default staying the identity (today's one template makes the two indistinguishable, but the
   * default has to be right regardless of that coincidence).
   */
  private roomFit: RoomFit = IDENTITY_ROOM_FIT;

  /**
   * A node being carried out of the tray. It has no lock — it does not exist on the server
   * yet — but it is the most obviously moving thing in the room, so it gets the same fade.
   */
  private carried: { nodeId: string; color: string } | null = null;

  /** Nodes and strokes that server truth has already dropped, still visible while their
   *  material eases toward invisible — see `queueFade`/`tickFades`. */
  private readonly fading: FadingMesh[] = [];

  /** Stagger bookkeeping for `queueFade` — see `nextFadeStagger`'s own doc comment. Reset
   *  implicitly: `lastFadeQueuedAtMs` starts at `-Infinity`, so the very first fade always
   *  measures an "elapsed" longer than `FADE_STAGGER_RESET_GAP_MS` and starts the ripple at
   *  index 0 regardless of `fadeStaggerIndex`'s own unread initial value. */
  private fadeStaggerIndex = -1;
  private lastFadeQueuedAtMs = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly scene: Scene,

    // Not private: `SnapIndicator` (`possession/SnapIndicator.ts`) parents its own mesh here
    // too, the same way every node mesh built below does, so it needs to read this from
    // outside — everything else on this class still only ever writes through `meshFor`/etc.
    readonly roomOrigin: TransformNode,

    // Defaulted, matching NodeMeshFactory's own optional-parameter shape — every existing test
    // still gets its own fresh, empty library. `mountRoomRoute.ts` is the one real caller that
    // passes its own instance explicitly, so `LobbyView`'s miniatures can share the identical,
    // already-preloaded library this room's own nodes are built from (SPEC-191) rather than each
    // one silently building and preloading nothing of its own.
    models: ModelLibrary = new ModelLibrary(scene),
  ) {
    this.factory = new NodeMeshFactory(scene, roomOrigin, models);
    this.connections = new ConnectionRenderer(scene, roomOrigin);
    this.labels = new LabelRenderer(scene, roomOrigin);
  }

  /** The node catalogue, supplied once at boot before any node is built. */
  setCatalog(types: NodeTypeDto[]): void {
    this.factory.setCatalog(types);
  }

  /**
   * Told the room's own floorplan — once at boot, right after `sceneRoot.buildRoom` first runs,
   * and again on every resync, since a reconnect can land in a room whose template genuinely
   * differs from the one this session started with (`main.ts`'s own `hub.onResynced`, which
   * rebuilds the room shell from scratch for the identical reason). Nothing already rendered is
   * corrected retroactively by this call alone — the caller's own `onReset`/`onNodeUpserted`
   * pass, which happens right alongside it in both real call sites, is what repaints with the
   * fresh fit.
   */
  setRoomFit(floorplan: FloorExtent): void {
    this.roomFit = computeRoomFit(floorplan);
  }

  /**
   * Canonical -> this room's own floor. Exposed for the authoring code that has to cross the
   * same boundary this class crosses on every render — `PossessionManager`/`PlacementManager`
   * read a node's position off a mesh in *room* space (whatever `toRoomSpace` last painted it
   * at) and have to convert it back before it becomes anything sent over the wire.
   */
  toRoomSpace(position: Point3): Vector3 {
    const fitted = this.roomFit.toRoom(position);
    return new Vector3(fitted.x, fitted.y, fitted.z);
  }

  /** This room's own floor -> canonical — the inverse of `toRoomSpace`, same callers. */
  toCanonical(position: Point3): Vector3 {
    const fitted = this.roomFit.toCanonical(position);
    return new Vector3(fitted.x, fitted.y, fitted.z);
  }

  /**
   * A node exactly as given, except its transform's position is expressed in this room's own
   * floor rather than the diagram's canonical space — what every mesh-facing call
   * (`factory.create`/`factory.refresh`) is actually built from. `NodeMeshFactory` itself stays
   * entirely unaware this boundary exists: it is hazardous to teach a mesh-construction class
   * about fitting when nothing about a mesh's own geometry is fitted, only its position — see
   * CLAUDE.md's own "One mesh per node is load-bearing" for why a second transform layered onto
   * the mesh (rather than converting the number that goes in) was never on the table.
   */
  private fitNodeToRoom(node: NodeDto): NodeDto {
    const roomPosition = this.roomFit.toRoom(node.transform.position);
    return { ...node, transform: { ...node.transform, position: roomPosition } };
  }

  /** Preloads model-backed types' .glb geometry. Call once at boot, after setCatalog and before
   *  the first node is built — see NodeMeshFactory.preloadModels. */
  async preloadModels(types: NodeTypeDto[]): Promise<void> {
    await this.factory.preloadModels(types);
  }

  /** A model-backed type's preloaded template, if one exists — PaletteUI clones the same
   *  template for a tray mini rather than loading or building a second copy of its own. Typed
   *  as `Mesh`, not `AbstractMesh`: the caller feeds this straight into `cloneModelTemplate`,
   *  which needs the narrower type. */
  modelTemplateFor(key: string): Mesh | undefined {
    return this.factory.modelTemplateFor(key);
  }

  /** The model library's boot-time load outcome, for SessionScreen — see
   *  NodeMeshFactory.modelSummary / ModelLibrary.summary. */
  modelSummary(): ModelLoadSummary {
    return this.factory.modelSummary();
  }

  /**
   * Points every name plate at the viewer.
   *
   * Per frame, and registered by the caller so frame order stays in one place. A yaw-billboard
   * follows the *camera*, not the thing it labels, so unlike edges this cannot be refreshed only
   * when something moves. Anchors are re-read here too, which keeps a label attached to a node
   * being dragged without the possession manager knowing labels exist.
   */
  updateLabels(): void {
    for (const [nodeId, mesh] of this.meshes) {
      if (this.labels.has(nodeId)) {
        this.labels.anchor(nodeId, this.labelAnchorFor(mesh));
      }
    }

    if (this.state) {
      for (const connection of this.state.connections.values()) {
        if (!this.labels.has(connection.id)) {
          continue;
        }

        const from = this.positionOf(connection.fromNodeId);
        const to = this.positionOf(connection.toNodeId);

        if (from && to) {
          // Midpoint, lifted clear of the tube so the plate does not intersect the line it
          // belongs to.
          this.labels.anchor(
            connection.id,
            from.add(to).scale(0.5).add(new Vector3(0, LABEL_CLEARANCE, 0)),
          );
        }
      }
    }

    this.labels.update();
  }

  /**
   * Just above the node's bounding box, so a plate clears the shape it names whatever the
   * silhouette's height happens to be.
   */
  private labelAnchorFor(mesh: AbstractMesh): Vector3 {
    // Forced: `updateLabels` runs from `onBeforeRenderObservable` (main.ts), and an unforced
    // read here can return last frame's world matrix regardless of registration order relative
    // to whatever moved the node this frame (CLAUDE.md, "An unforced `computeWorldMatrix` can
    // return last frame's answer"). Without this, a dragged node's plate trails it by a frame.
    mesh.computeWorldMatrix(true);
    const bounding = mesh.getBoundingInfo().boundingBox;

    return new Vector3(
      mesh.position.x,
      bounding.maximumWorld.y + LABEL_CLEARANCE + this.labels.plateHeight / 2,
      mesh.position.z,
    );
  }

  onReset(state: RoomState): void {
    this.state = state;

    // A reconnect can arrive at a room that changed while we were away, so rebuild rather
    // than diff: correctness matters more than a few dozen mesh allocations. Anything mid-fade
    // is flushed outright, not eased the rest of the way — a resync is a hard correctness
    // event, not a cosmetic one, and the item it belonged to may not even exist in the new
    // snapshot to fade "toward".
    this.flushFades();

    for (const mesh of this.meshes.values()) {
      mesh.material?.dispose();
      mesh.dispose();
    }

    this.meshes.clear();
    this.connections.clear();
    this.labels.clear();

    // Not mere hygiene — `labelStatusOverrides` left uncleared here breaks the *very next*
    // paint. `labels.clear()` above just wiped every plate outright, and the loop below is
    // about to call `onNodeUpserted` for every node in the fresh snapshot — but that method's
    // own guard (`if (!this.labelStatusOverrides.has(node.id))`) would see a stale entry for a
    // node id that survived the reset and wrongly conclude a status is still active, skipping
    // the repaint entirely and leaving that node with no plate at all until something else
    // happens to clear it. Confirmed by reverting just this line: the very next node upserted
    // with a since-cleared id silently lost its label. `nodeText` is rebuilt fresh by that same
    // loop regardless; clearing it here first is ordinary hygiene, not load-bearing the way
    // `labelStatusOverrides` is.
    this.nodeText.clear();
    this.labelStatusOverrides.clear();

    // Committed strokes are rebuilt from the snapshot below, same as nodes and
    // connections. In-progress ones are not durable and were never part of it — cleared
    // here purely as hygiene, in case a reconnect landed mid-someone-else's-stroke.
    for (const mesh of this.strokeMeshes.values()) {
      mesh.material?.dispose();
      mesh.dispose();
    }

    this.strokeMeshes.clear();
    this.clearLiveStrokes();

    // The scan tool's own glow state (see `setScanSelection`/`beginScanPulse`) references
    // stroke ids that may no longer exist post-reset — cleared here for the same reconnect
    // hygiene as `strokeMeshes`/`liveStrokes` just above, not because leaving it would leak
    // anything (it holds ids and numbers, no disposable Babylon objects of its own).
    this.scanSelectedIds = new Set();
    this.scanPulseIds = null;
    this.scanFadingGlow.clear();

    for (const node of state.nodes.values()) {
      this.onNodeUpserted(node);
    }

    for (const connection of state.connections.values()) {
      this.onConnectionUpserted(connection);
    }

    for (const stroke of state.strokes.values()) {
      this.onStrokeUpserted(stroke);
    }

    this.onLocksChanged();
  }

  onNodeUpserted(node: NodeDto): void {
    let mesh = this.meshes.get(node.id);

    // `node.transform.position` is canonical (SPEC-131) — fitted to this room's own floor only
    // for the mesh-facing calls below. Everything else in this method (labels, text, edges)
    // reads `node` unchanged; only a mesh's actual position ever needs the room-space value.
    const fitted = this.fitNodeToRoom(node);

    if (!mesh) {
      mesh = this.factory.create(fitted);
      this.meshes.set(node.id, mesh);
    } else {
      this.factory.refresh(mesh, fitted);
    }

    // Reapplied here as well as on lock changes, because a mesh can be built *after* the
    // lock that applies to it — an optimistic node is carried before it exists on the server.
    this.factory.setHeldLook(mesh, this.holdColorFor(node.id));

    // Normalized to '' rather than stored as-is: `NodeDto.text` types nullable (REST codegen,
    // CLAUDE.md's own "a hand losing tracking has to type as nullable"), and `LabelRenderer.set`
    // already treats an empty string as "no plate" — the same fallback `setLabelStatus` below
    // needs when restoring an unlabelled node's real (absent) text. Updated unconditionally,
    // regardless of whether a status overlay is currently painted over it — this is what lets
    // `setLabelStatus(id, null)`'s own eventual restore already have the freshest text ready,
    // the instant it happens, with no second round trip needed.
    this.nodeText.set(node.id, node.text ?? '');

    // A label plate, and only when the shape has been named — *unless* a status overlay
    // (`labelStatusOverrides`, `setLabelStatus`) is currently active for this node, in which
    // case it keeps winning until whatever set it explicitly clears it
    // (`NodeLabelController.handleNodeUpdated`/`handleFailed`/its own timeout backstop) rather
    // than any `NodeUpdated` that happens to arrive being treated as implicit permission to
    // let it go. In the one case this actually happens today — this exact broadcast *is* the
    // status's own successful completion — `NodeLabelController` fires that explicit clear
    // immediately afterward in the same tick (main.ts calls `state.upsertNode` then
    // `nodeLabel.handleNodeUpdated` back to back), so the visible result is identical; the
    // point of not repainting here directly is to not *depend* on that being the only case
    // that can ever produce a `NodeUpdated` for a node with an active status.
    if (!this.labelStatusOverrides.has(node.id)) {
      this.labels.set(node.id, node.text);
    }

    this.refreshEdgesFor(node.id);
  }

  onNodeRemoved(nodeId: string): void {
    const mesh = this.meshes.get(nodeId);

    if (mesh) {
      this.meshes.delete(nodeId);
      this.queueFade(mesh);
    }

    this.nodeText.delete(nodeId);
    this.labelStatusOverrides.delete(nodeId);
    this.labels.remove(nodeId);
  }

  /**
   * Shows (or clears) a transient status directly on a node's own label plate —
   * `NodeLabelController`'s own voice-labelling feature, wired through `main.ts`. `text: null`
   * restores whatever the server's own text for this node currently is (`nodeText`), or clears
   * the plate entirely if the node has never had one. Clearing a status that was never active
   * is harmless without a special case of its own here: `nodeText` already agrees with whatever
   * `labels` is currently showing in that case, and `LabelRenderer.set`'s own documented
   * idempotency ("repaints only when the text actually changed") means handing it the same
   * text back a second time never touches the mesh.
   *
   * Deliberately keyed and rendered exactly like `onNodeUpserted`'s own `labels.set(node.id,
   * node.text)` — same map, same key — so a status overlay and the node's real label can never
   * both exist as separate plates at once; one always simply replaces the other.
   *
   * Guarded on `meshes.has(nodeId)` for a non-null `text`: a capture's own node can be deleted
   * out from under it while the round trip to Gemini is still in flight — exactly what
   * `LabelTranscriptionPipeline`'s own "not-found" failure reason means — and this must not
   * resurrect a floating, mesh-less label plate for a node that is already gone. `onNodeRemoved`
   * already tore down any plate and cleared this node from both maps by the time that failure
   * can arrive, so painting over it here would be the only thing putting it back.
   */
  setLabelStatus(nodeId: string, text: string | null): void {
    if (text !== null) {
      if (!this.meshes.has(nodeId)) {
        return;
      }

      this.labelStatusOverrides.add(nodeId);
      this.labels.set(nodeId, text);
      return;
    }

    this.labelStatusOverrides.delete(nodeId);
    this.labels.set(nodeId, this.nodeText.get(nodeId) ?? null);
  }

  onConnectionUpserted(connection: ConnectionDto): void {
    const from = this.positionOf(connection.fromNodeId);
    const to = this.positionOf(connection.toNodeId);

    this.labels.set(connection.id, connection.label);

    // An edge whose endpoints have not arrived yet is drawn when they do; event order
    // across a reconnect is not guaranteed.
    if (from && to) {
      this.connections.render(
        connection,
        from,
        to,
        this.radiusOf(connection.toNodeId),
        this.radiusOf(connection.fromNodeId),
      );
    }
  }

  onConnectionRemoved(connectionId: string): void {
    this.connections.remove(connectionId);
    this.labels.remove(connectionId);
  }

  /**
   * Renders a committed stroke: your own commit's returned result, a peer's StrokeCreated,
   * or one already on the board from the snapshot. In its own real ink colour, always —
   * unlike an in-progress one, a committed stroke never rides in anyone's presence colour.
   */
  onStrokeUpserted(stroke: StrokeDto): void {
    // A commit landing — this id's own, or a peer's — always supersedes whatever
    // in-progress rendering existed for it, so there is never a frame with both a live and
    // a committed mesh for the same stroke visible at once.
    this.endLiveStroke(stroke.id);

    const points = stroke.points.map((p) => new Vector3(p.x, p.y, p.z));
    this.rebuildStrokeMesh(this.strokeMeshes, stroke.id, points, stroke.color, toStrokeWidth(stroke.width));
  }

  onStrokeRemoved(strokeId: string): void {
    const mesh = this.strokeMeshes.get(strokeId);

    if (!mesh) {
      // Idempotent by construction, matching RoomState.removeStroke's own idempotence
      // (CLAUDE.md, "removeStroke ... idempotent on purpose") — including a second wipe
      // arriving for a stroke already mid-fade, which by this point has already been taken
      // out of strokeMeshes below, so this is a no-op rather than a double-fade or a double
      // dispose.
      return;
    }

    this.strokeMeshes.delete(strokeId);
    this.queueFade(mesh);
  }

  /** Highlights the selected edge, or clears the highlight. */
  selectConnection(connectionId: string | null): void {
    const previous = this.connections.selected;
    this.connections.select(connectionId);

    // Both the old and the new edge have to be redrawn: the highlight is a thickness, applied
    // when the edge is next rendered rather than held as separate state.
    for (const id of [previous, connectionId]) {
      const connection = id === null ? undefined : this.state?.connections.get(id);

      if (connection) {
        this.onConnectionUpserted(connection);
      }
    }
  }

  onLocksChanged(): void {
    if (!this.state) {
      return;
    }

    // A node in somebody's hands goes semi-transparent in their colour. That is the whole
    // "someone else has this" affordance, and the same treatment shows your own grab.
    for (const [nodeId, mesh] of this.meshes) {
      this.factory.setHeldLook(mesh, this.holdColorFor(nodeId));
    }
  }

  /**
   * Marks a node as being carried out of the tray, or clears the mark.
   *
   * Separate from locks because a carried node has none: it is created optimistically and
   * only exists on the server once it is let go of.
   */
  markCarried(nodeId: string | null): void {
    const previous = this.carried?.nodeId ?? null;

    this.carried =
      nodeId === null
        ? null
        : { nodeId, color: this.state?.holderColor(nodeId) ?? this.selfColor() };

    for (const id of [previous, nodeId]) {
      if (id === null) {
        continue;
      }

      const mesh = this.meshes.get(id);

      if (mesh) {
        this.factory.setHeldLook(mesh, this.holdColorFor(id));
      }
    }
  }

  private holdColorFor(nodeId: string): string | null {
    if (this.carried?.nodeId === nodeId) {
      return this.carried.color;
    }

    return this.state?.holderColor(nodeId) ?? null;
  }

  private selfColor(): string {
    return this.state?.self?.colorHex ?? '#FFFFFF';
  }

  onPeersChanged(): void {
    // Presence is surfaced by the overlay in M2; avatars arrive with M5.
  }

  /**
   * Applies a streamed pose for a node someone is holding. Never written back to state —
   * these are dropped by the server, and the durable write happens once on release.
   *
   * `update.p` is canonical, not this room's own space — A2 made `StreamNodeTransform` fan out
   * to everyone with the diagram open, including a peer in a *different* room
   * (plan/diagram-extraction.md, "What A2 actually cost": "a streamed canonical position needs
   * fitting at the receiver exactly like a stored one"), and the sender itself converts its own
   * held mesh's room-space position to canonical before ever calling `streamNodeTransform`
   * (`PossessionManager.trackHeld`) — so this is the one place a live pose actually arrives, and
   * fitting it here is what makes both the holder's own room and every other room watching the
   * same diagram agree on where the node visibly is.
   */
  applyLiveTransform(update: NodeTransformUpdate): void {
    const mesh = this.meshes.get(update.nodeId);

    // Exact-length, not merely "at least" — `vec3FromArray`/`quatFromArray` (net/packing.ts)
    // require exactly 3/4 components and throw otherwise, so a `< 3`/`< 4` guard here would
    // let a wrong-but-longer array (say, 4 components where 3 were expected) straight through
    // and crash one call below instead of the intended quiet drop. Unreachable with today's
    // server — every producer always emits exactly 3/4 — but the guard should actually deliver
    // the robustness its shape implies rather than merely resemble it
    // (plan/open-items.md, "`length < 3` guards claim a robustness they do not deliver").
    if (!mesh || update.p.length !== 3 || update.r.length !== 4) {
      return;
    }

    mesh.position.copyFrom(this.toRoomSpace(vec3FromArray(update.p)));
    (mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(quatFromArray(update.r));
    mesh.scaling.setAll(update.s);

    this.refreshEdgesFor(update.nodeId);
  }

  /**
   * Your own stroke, drawn locally and not yet committed. The caller (DrawingManager)
   * already owns the full, growing point list — captured frame by frame — so this
   * replaces the rendered path outright rather than accumulating onto anything here.
   * Rendered in your own presence colour, never the pen's ink colour: see
   * plan/whiteboard.md, "a stroke being watched mid-draw renders in the drawer's own
   * presence colour ... including the person who drew it." `width` is the drawer's own
   * currently-selected marker width (`DrawingManager` reads `ToolMode.markerWidth`), so a
   * stroke drawn fat is already shown fat while it streams, not only once it commits.
   */
  updateLiveStroke(strokeId: string, points: Vector3[], colorHex: string, width: StrokeWidth): void {
    this.liveStrokePoints.set(strokeId, points);
    this.rebuildStrokeMesh(this.liveStrokes, strokeId, points, colorHex, width);
  }

  /**
   * A remote peer's stroke, still in progress. Never written back to RoomState — these
   * points are never persisted, the same rule applyLiveTransform already follows for a
   * held node's pose (see its own remarks above).
   *
   * Unlike updateLiveStroke, StrokePointUpdate carries only the points new since the
   * sender's last flush, so this accumulates onto whatever this id already has rather
   * than replacing it — liveStrokePoints is exactly that accumulator, and it is the whole
   * answer to "how is a remote in-progress stroke rendered without entering RoomState":
   * nowhere else does this client keep a stroke's points outside RoomState.
   *
   * Always rendered at `StrokeWidths.Medium` while it streams — `StrokePointUpdate` carries no
   * width (CLAUDE.md, "streaming messages are never persisted," and this one is not even a
   * durable field), so the sender's actual chosen width is not known until `onStrokeUpserted`
   * applies the real, committed `StrokeDto.width`, at which point the mesh is rebuilt at the
   * right size anyway (`onStrokeUpserted` always calls `endLiveStroke` first).
   */
  applyStrokePoint(update: StrokePointUpdate, colorHex: string): void {
    const existing = this.liveStrokePoints.get(update.strokeId) ?? [];
    const points = existing.concat(update.points.map((p) => vec3FromArray(p)));

    this.liveStrokePoints.set(update.strokeId, points);
    this.rebuildStrokeMesh(this.liveStrokes, update.strokeId, points, colorHex, StrokeWidths.Medium);
  }

  /** Ends an in-progress stroke's rendering — called once it commits (superseded by
   *  onStrokeUpserted) or is abandoned, so nothing is left both live and orphaned. */
  endLiveStroke(strokeId: string): void {
    this.liveStrokePoints.delete(strokeId);

    const mesh = this.liveStrokes.get(strokeId);

    if (!mesh) {
      return;
    }

    mesh.material?.dispose();
    mesh.dispose();
    this.liveStrokes.delete(strokeId);
  }

  meshFor(nodeId: string): AbstractMesh | undefined {
    return this.meshes.get(nodeId);
  }

  nodeIdFor(mesh: AbstractMesh): string | undefined {
    const metadata = mesh.metadata as { nodeId?: string } | undefined;
    return metadata?.nodeId;
  }

  dispose(): void {
    this.flushFades();
    this.scanSelectedIds = new Set();
    this.scanPulseIds = null;
    this.scanFadingGlow.clear();

    for (const mesh of this.meshes.values()) {
      mesh.material?.dispose();
      mesh.dispose();
    }

    this.meshes.clear();
    this.connections.clear();
    this.labels.dispose();

    for (const mesh of this.strokeMeshes.values()) {
      mesh.material?.dispose();
      mesh.dispose();
    }

    this.strokeMeshes.clear();
    this.clearLiveStrokes();
  }

  private clearLiveStrokes(): void {
    for (const mesh of this.liveStrokes.values()) {
      mesh.material?.dispose();
      mesh.dispose();
    }

    this.liveStrokes.clear();
    this.liveStrokePoints.clear();
  }

  /**
   * Queues a mesh whose node or stroke server truth has already dropped for a fade-out rather
   * than an instant dispose. The mesh is already out of `meshes`/`strokeMeshes` by the time
   * this is called — the caller does that first — so nothing here can be found or reused by a
   * late-arriving update; a fresh create for the same id builds an entirely new mesh instead
   * (see `onStrokeUpserted`/`onNodeUpserted`), leaving this one to finish fading and dispose on
   * its own schedule, never resurrected.
   *
   * Not pickable for the same reason a fading thing should not still answer to input: this
   * mesh no longer exists in RoomState, so DevSim's own board/node/tray pick predicate
   * (`mesh.metadata?.nodeId !== undefined`) must not go on matching it just because the mesh
   * object itself has not been disposed yet.
   */
  private queueFade(mesh: AbstractMesh): void {
    const material = mesh.material;

    if (!(material instanceof StandardMaterial)) {
      // No alpha channel to animate — fall back to the old instant behaviour rather than
      // leaving the mesh stranded forever with nothing to ever dispose it.
      mesh.material?.dispose();
      mesh.dispose();
      return;
    }

    mesh.isPickable = false;

    const now = performance.now();
    const { delayMs, index } = nextFadeStagger(
      now - this.lastFadeQueuedAtMs,
      this.fadeStaggerIndex,
      FADE_STAGGER_STEP_MS,
      FADE_STAGGER_MAX_MS,
      FADE_STAGGER_RESET_GAP_MS,
    );

    this.fadeStaggerIndex = index;
    this.lastFadeQueuedAtMs = now;

    this.fading.push({
      mesh,
      material,
      startAlpha: material.alpha,
      delayMs,
      startedAtMs: now,
    });
  }

  /**
   * Advances every in-flight fade by one tick. Registered by the caller (`main.ts`) on the same
   * per-frame observable `updateLabels()` already runs from, so a fading mesh's alpha updates
   * every rendered frame regardless of whether anything else in the room is moving.
   */
  tickFades(): void {
    if (this.fading.length === 0) {
      return;
    }

    const now = performance.now();
    let writeIndex = 0;

    for (let i = 0; i < this.fading.length; i += 1) {
      const item = this.fading[i]!;
      const step = fadeAlphaAt(now - item.startedAtMs, item.delayMs, FADE_DURATION_MS, item.startAlpha);

      item.material.alpha = step.alpha;

      if (step.done) {
        item.material.dispose();
        item.mesh.dispose();
        continue;
      }

      this.fading[writeIndex] = item;
      writeIndex += 1;
    }

    this.fading.length = writeIndex;
  }

  /** Disposes every mesh currently mid-fade outright, with no further animation — used where
   *  correctness matters more than the cosmetic lingering (`onReset`, `dispose`). */
  private flushFades(): void {
    for (const item of this.fading.splice(0)) {
      item.material.dispose();
      item.mesh.dispose();
    }
  }

  /**
   * State 1, "live selection, while sweeping" — `ScanTool.tick()` calls this on every render
   * frame with whatever `strokesInRange` currently returns for the scan line's current position
   * and length, so the glowing set always matches exactly what would be caught if the trigger
   * released right now — never a snapshot taken once when the sweep began. That is the property
   * the owner's own headset report needs: seeing *before* committing to a scan (and while
   * adjusting the line's length) exactly what is about to be caught, not finding out only after
   * the round trip that it grabbed more (or less) of the board than intended.
   *
   * Every id no longer in `strokeIds` is eased back to `STROKE_EMISSIVE_SCALE`; every id in it
   * is set to `STROKE_SCAN_GLOW_SCALE`. A stroke currently mid-pulse or mid-fade-out (states 2/3
   * below) is left alone — the sweep and an in-flight scan cannot overlap in practice (`ScanTool`
   * stops calling this once a scan starts processing), but this keeps the two states from
   * fighting over the same mesh if they ever did.
   */
  setScanSelection(strokeIds: ReadonlySet<string>): void {
    for (const id of this.scanSelectedIds) {
      if (!strokeIds.has(id) && !this.scanPulseIds?.has(id) && !this.scanFadingGlow.has(id)) {
        this.applyStrokeEmissiveScale(id, STROKE_EMISSIVE_SCALE);
      }
    }

    for (const id of strokeIds) {
      this.applyStrokeEmissiveScale(id, STROKE_SCAN_GLOW_SCALE);
    }

    this.scanSelectedIds = strokeIds;
  }

  /**
   * State 2, "pulsing during the round trip" — called once, when a scan's transcription request
   * goes out, with exactly the strokes that were caught. Routed through `setScanSelection`
   * itself (with an empty set) rather than clearing the field directly, so every previously
   * selected id — the live-selection glow (state 1) always tracks exactly the strokes about to
   * be caught, so this is normally every id about to pulse — is properly eased back to
   * `STROKE_EMISSIVE_SCALE` through the one code path that already knows how, rather than a
   * second, parallel reset. The pulse itself takes over on the very next `tickScanGlow`, so any
   * one-frame dip back to baseline in between is not visible in practice. Also resets the pulse
   * clock, so the breathing starts from the top of its cycle rather than wherever a previous
   * pulse happened to leave off.
   */
  beginScanPulse(strokeIds: ReadonlySet<string>): void {
    this.scanFadingGlow.clear();
    this.setScanSelection(new Set());
    this.scanPulseIds = strokeIds;
    this.scanPulseElapsedS = 0;
  }

  /**
   * Ends the round-trip pulse (state 2). Two callers, two different endings:
   *
   * - **A successful scan** (`fade: false`) — the caught strokes are about to be deleted
   *   (`ScanTool.finishScan` calls `hub.deleteStroke` right after this), so there is nothing to
   *   ease back to baseline; `onStrokeRemoved`'s own fade-out takes over immediately.
   * - **Nothing found, or a failure** (`fade: true`) — state 3, "fade out on nothing found."
   *   Nothing is deleted, so the glow has to visibly come back down rather than snapping off,
   *   which would read as an error rather than "I looked, there was nothing there." Each
   *   stroke's fade starts from its own current pulse value (`pulseScale` at the moment this is
   *   called), not a fixed peak, so the ease is continuous rather than jumping first.
   */
  endScanPulse(fade: boolean): void {
    const ids = this.scanPulseIds;
    this.scanPulseIds = null;

    if (!ids) {
      return;
    }

    if (!fade) {
      // The caller is about to delete these strokes; nothing here needs to touch the material.
      return;
    }

    const currentScale = pulseScale(
      this.scanPulseElapsedS,
      STROKE_SCAN_GLOW_SCALE,
      STROKE_SCAN_PULSE_AMPLITUDE,
      STROKE_SCAN_PULSE_PERIOD_S,
    );

    for (const id of ids) {
      this.scanFadingGlow.set(id, { startScale: currentScale, elapsedMs: 0 });
    }
  }

  /**
   * Advances the pulse (state 2) and any in-progress fade-outs (state 3) by one tick.
   * Registered by the caller (`main.ts`) on the same per-frame observable `tickFades()` already
   * runs from — not `requestAnimationFrame`, per CLAUDE.md's own warning that rAF is throttled
   * inside an active WebXR session while `onBeforeRenderObservable` keeps firing every real XR
   * frame, the same reason `ScanTool`'s own gesture tick already uses it. `deltaSeconds` is the
   * engine's own per-frame delta (`Scene.getEngine().getDeltaTime() / 1000`), read once by the
   * caller and passed in here rather than read twice — keeps this class free of any direct
   * engine dependency beyond the `Scene` it already holds for mesh construction.
   */
  tickScanGlow(deltaSeconds: number): void {
    if (this.scanPulseIds) {
      this.scanPulseElapsedS += deltaSeconds;
      const scale = pulseScale(
        this.scanPulseElapsedS,
        STROKE_SCAN_GLOW_SCALE,
        STROKE_SCAN_PULSE_AMPLITUDE,
        STROKE_SCAN_PULSE_PERIOD_S,
      );

      for (const id of this.scanPulseIds) {
        this.applyStrokeEmissiveScale(id, scale);
      }
    }

    if (this.scanFadingGlow.size > 0) {
      const deltaMs = deltaSeconds * 1000;

      for (const [id, fade] of this.scanFadingGlow) {
        const elapsedMs = fade.elapsedMs + deltaMs;
        const step = glowFadeOutAt(elapsedMs, STROKE_SCAN_GLOW_FADE_MS, fade.startScale, STROKE_EMISSIVE_SCALE);

        this.applyStrokeEmissiveScale(id, step.scale);

        if (step.done) {
          this.scanFadingGlow.delete(id);
        } else {
          this.scanFadingGlow.set(id, { startScale: fade.startScale, elapsedMs });
        }
      }
    }
  }

  /** Sets a committed stroke's emissive colour to its own stored diffuse colour (the real ink
   *  colour a stroke was drawn in — `rebuildStrokeMesh`) scaled by `scale`. The one place every
   *  scan-glow state above actually writes to a material; a missing mesh (already deleted, or
   *  never a real committed stroke — the scan tool only ever catches ones that are) is silently
   *  a no-op rather than an error, matching `onStrokeRemoved`'s own idempotence. */
  private applyStrokeEmissiveScale(strokeId: string, scale: number): void {
    const material = this.strokeMeshes.get(strokeId)?.material;

    if (material instanceof StandardMaterial) {
      material.emissiveColor = material.diffuseColor.scale(scale);
    }
  }

  /**
   * Builds or replaces the mesh for one stroke id in the given registry (strokeMeshes for
   * committed strokes, liveStrokes for in-progress ones) — the two are rendered by the
   * exact same geometry, differing only in which map holds them and where their colour and
   * point list come from.
   *
   * Always dispose-and-recreate rather than an in-place update: Babylon's CreateTube
   * `instance` option can only update a path with the *same* point count as before ("The
   * pathArray HAS to have the SAME number of points as the previous one"), which a growing
   * stroke never has.
   */
  private rebuildStrokeMesh(
    registry: Map<string, Mesh>,
    strokeId: string,
    points: Vector3[],
    colorHex: string,
    width: StrokeWidth,
  ): void {
    const previous = registry.get(strokeId);
    previous?.material?.dispose();
    previous?.dispose();

    if (points.length === 0) {
      registry.delete(strokeId);
      return;
    }

    const material = new StandardMaterial(`stroke-${strokeId}`, this.scene);
    const color = Color3.FromHexString(colorHex);
    material.diffuseColor = color;
    material.emissiveColor = color.scale(STROKE_EMISSIVE_SCALE);
    material.specularColor = Color3.Black();

    const radius = radiusForWidth(width);

    // A single point (a tap with no drag) has no direction for a tube to follow — a small
    // dot stands in, so touching the board once still leaves a mark, the way a real marker
    // would.
    const mesh: Mesh =
      points.length === 1
        ? this.buildStrokeDot(strokeId, points[0]!, radius)
        : CreateTube(`stroke-${strokeId}`, { path: points, radius }, this.scene);

    mesh.material = material;

    // Scenery to the hand, same as an edge's tube: the eraser's own contact test samples
    // a stroke's stored point list directly (DrawingManager), not a scene pick against this
    // mesh, so picking would only ever compete with grabbing.
    mesh.isPickable = false;
    mesh.parent = this.roomOrigin;

    registry.set(strokeId, mesh);
  }

  private buildStrokeDot(strokeId: string, point: Vector3, radius: number): Mesh {
    const mesh = CreateSphere(`stroke-${strokeId}`, { diameter: radius * 2, segments: 6 }, this.scene);
    mesh.position.copyFrom(point);

    return mesh;
  }

  /**
   * Bounding radius of a node, so an arrowhead can sit against it rather than inside it.
   *
   * Zero when the node is not built yet, which puts the arrowhead at the endpoint — wrong, but
   * only until the mesh arrives and the edge is redrawn.
   */
  private radiusOf(nodeId: string): number {
    return this.meshes.get(nodeId)?.getBoundingInfo().boundingSphere.radiusWorld ?? 0;
  }

  /**
   * Edges follow their endpoints, so any node that moves drags its edges with it. Scoped
   * to the moving node rather than redrawing every edge each frame — a held node moves
   * every frame and a busy room can hold plenty of unrelated edges.
   *
   * Public because a locally held node is moved directly by the possession manager, without
   * going through state.
   */
  refreshEdgesFor(nodeId: string): void {
    if (!this.state) {
      return;
    }

    for (const connection of this.state.connectionsTouching(nodeId)) {
      this.onConnectionUpserted(connection);
    }
  }

  /**
   * Where an endpoint currently is.
   *
   * The mesh, not the DTO. A held node is moved directly — by the local possession manager
   * every frame, or by an arriving streamed pose — and the DTO still holds the pose from
   * before the grab. Reading the DTO would leave edges anchored where the node used to be.
   */
  private positionOf(nodeId: string): Vector3 | null {
    const mesh = this.meshes.get(nodeId);

    if (mesh) {
      return mesh.position;
    }

    const node = this.state?.nodes.get(nodeId);

    if (!node) {
      return null;
    }

    // Canonical, same as every other read off `node.transform` — fitted to this room, same as
    // the mesh this falls back from would have been.
    return this.toRoomSpace(node.transform.position);
  }
}
